import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, ThumbsUp, Linkedin, Award, Users, Briefcase, 
  MapPin, Clock, Share2, Check, Loader2, ExternalLink, Heart, X, ShieldCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NomineeProfilePage = () => {
  const { uniqueId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token, login } = useAuth();
  const { toast } = useToast();
  
  const [nominee, setNominee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [voted, setVoted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [selectedVoterType, setSelectedVoterType] = useState('');

  const handleLinkedInSignIn = () => {
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    login();
  };

  const questions = [
    'What inspired you to start your journey in Talent Acquisition?',
    'Describe your proudest moment or success story as a recruiter or TA leader.',
    'What are the top three traits that define your approach to recruitment?',
    'How do you build trust and relationships with candidates and clients?',
    'Share an example where you solved a complex hiring challenge.',
    'How do you leverage technology, tools, or data in your recruitment strategy?',
    'What\'s one thing you\'re doing differently that sets you apart from others?',
    'How have you contributed to your team\'s or company\'s overall success?',
    'How do you continuously learn and stay ahead in the evolving TA landscape?',
    'What does "excellence in talent acquisition" mean to you personally?'
  ];

  const voterTypes = [
    { value: 'candidate', label: 'Candidate', icon: '👤', description: 'Someone you placed or interviewed' },
    { value: 'peer', label: 'Industry Peer', icon: '🤝', description: 'Fellow recruiter or TA professional' },
    { value: 'senior_junior', label: 'Senior/Junior Colleague', icon: '👔', description: 'More/less experienced TA professional' },
    { value: 'manager', label: 'Company Manager', icon: '💼', description: 'Your direct or skip-level manager' },
    { value: 'hr', label: 'HR Team Member', icon: '🎯', description: 'HR professional from your company' }
  ];

  useEffect(() => {
    fetchNomineeProfile();
  }, [uniqueId]);

  const fetchNomineeProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/nominee/${uniqueId}`);
      const data = await response.json();
      
      if (data.success) {
        setNominee(data.nominee);
      } else {
        toast({
          title: 'Nominee Not Found',
          description: 'This nomination link is invalid',
          variant: 'destructive'
        });
        navigate('/awards');
      }
    } catch (error) {
      console.error('Error fetching nominee:', error);
      toast({
        title: 'Error',
        description: 'Failed to load nominee profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async () => {
    if (!isAuthenticated) {
      handleLinkedInSignIn();
      return;
    }

    if (!selectedVoterType) {
      toast({
        title: 'Select Voter Type',
        description: 'Please select your relationship with the nominee',
        variant: 'destructive'
      });
      return;
    }

    setVoting(true);

    try {
      // Get LinkedIn ID from user profile (assuming it's stored)
      const voterLinkedinId = user.linkedin_id || user.user_id;
      
      const response = await fetch(`${BACKEND_URL}/api/awards/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nomination_id: nominee.nomination_id,
          voter_type: selectedVoterType,
          voter_linkedin_id: voterLinkedinId,
          voter_name: user.name,
          voter_email: user.email
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setVoted(true);
        setShowThankYou(true);
        setNominee(prev => ({ ...prev, total_votes: data.total_votes }));
      } else {
        throw new Error(data.detail || 'Vote submission failed');
      }
    } catch (error) {
      toast({
        title: 'Vote Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!nominee) return null;

  const categoryInfo = {
    rookie: { name: 'Best TA Rookie Award', color: 'text-green-600', bg: 'bg-green-50' },
    emerging_leader: { name: 'Best Emerging TA Leader Award', color: 'text-blue-600', bg: 'bg-blue-50' },
    excellence: { name: 'Best TA Excellence Award', color: 'text-purple-600', bg: 'bg-purple-50' },
    manager: { name: 'Best TA Manager Award', color: 'text-orange-600', bg: 'bg-orange-50' },
    senior_leader: { name: 'Best TA Senior Leader Award', color: 'text-yellow-600', bg: 'bg-yellow-50' }
  };

  const category = categoryInfo[nominee.category] || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50 py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <Button onClick={() => navigate('/awards')} variant="outline" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Awards
        </Button>

        {/* Nominee Header */}
        <Card className="mb-8 shadow-2xl border-2 border-purple-200">
          <CardContent className="pt-8">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Profile Picture */}
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-purple-400 to-blue-500 flex-shrink-0">
                {nominee.profile_picture ? (
                  <img src={nominee.profile_picture} alt={nominee.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl font-bold text-white">
                    {nominee.name.charAt(0)}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-slate-900 mb-2">{nominee.name}</h1>
                
                <div className="flex flex-wrap gap-3 mb-4">
                  {nominee.job_title && (
                    <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {nominee.job_title}
                    </Badge>
                  )}
                  {nominee.company && (
                    <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {nominee.company}
                    </Badge>
                  )}
                  {nominee.experience && (
                    <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {nominee.experience}+ years
                    </Badge>
                  )}
                </div>

                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${category.bg} mb-4`}>
                  <Award className={`w-5 h-5 ${category.color}`} />
                  <span className={`font-semibold ${category.color}`}>{category.name}</span>
                </div>

                {nominee.linkedin_url && (
                  <a
                    href={nominee.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
                  >
                    <Linkedin className="w-5 h-5" />
                    View LinkedIn Profile
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>

              {/* Vote Count */}
              <div className="text-center bg-gradient-to-br from-purple-500 to-blue-500 text-white p-6 rounded-2xl shadow-lg">
                <Users className="w-8 h-8 mx-auto mb-2" />
                <div className="text-5xl font-bold">{nominee.total_votes}</div>
                <div className="text-sm font-semibold">Total Votes</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Voting Section */}
        {!voted && (
          <Card className="mb-8 shadow-lg border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <ThumbsUp className="w-6 h-6 text-green-600" />
                Vote for {nominee.name.split(' ')[0]}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Label className="text-lg font-semibold mb-3 block">Select Your Relationship:</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                {voterTypes.map((type) => (
                  <button
                    key={type.value}
                    data-testid={`voter-type-${type.value}`}
                    onClick={() => setSelectedVoterType(type.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedVoterType === type.value
                        ? 'border-green-500 bg-green-100 shadow-md'
                        : 'border-slate-200 hover:border-green-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">{type.icon}</div>
                    <div className="font-bold text-slate-900 mb-1">{type.label}</div>
                    <div className="text-xs text-slate-600">{type.description}</div>
                  </button>
                ))}
              </div>
              
              {isAuthenticated ? (
                <Button
                  data-testid="cast-vote-btn"
                  onClick={handleVote}
                  disabled={!selectedVoterType || voting}
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg py-6"
                >
                  {voting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting Vote...
                    </>
                  ) : (
                    <>
                      <ThumbsUp className="w-5 h-5 mr-2" />
                      Cast My Vote
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button
                    data-testid="vote-linkedin-signin-btn"
                    onClick={handleLinkedInSignIn}
                    disabled={!selectedVoterType}
                    size="lg"
                    className="w-full bg-[#0A66C2] hover:bg-[#004182] text-white font-bold text-lg py-6"
                  >
                    <Linkedin className="w-5 h-5 mr-2" />
                    {selectedVoterType ? 'Sign in with LinkedIn to Cast Your Vote' : 'Select your relationship above, then sign in to vote'}
                  </Button>
                  <p className="text-sm text-slate-600 flex items-center justify-center gap-2" data-testid="vote-auth-note">
                    <ShieldCheck className="w-4 h-4 text-green-600" />
                    LinkedIn verification keeps voting fair — it takes 10 seconds and you'll return right here.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {voted && (
          <Card className="mb-8 shadow-lg border-2 border-green-500 bg-green-50" data-testid="voted-confirmation-card">
            <CardContent className="pt-6 text-center">
              <Check className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You for Voting!</h3>
              <p className="text-slate-600">Your vote has been recorded successfully.</p>
            </CardContent>
          </Card>
        )}

        {/* Thank You Popup */}
        {showThankYou && (
          <div className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4" data-testid="thank-you-popup-overlay" onClick={() => setShowThankYou(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden text-center" data-testid="thank-you-popup" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-8 text-white relative">
                <button data-testid="thank-you-close" onClick={() => setShowThankYou(false)} className="absolute top-3 right-3 p-1 rounded hover:bg-white/20">
                  <X className="w-5 h-5" />
                </button>
                <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <Heart className="w-10 h-10 text-white fill-white" />
                </div>
                <h2 className="text-3xl font-bold mb-1">Thank You! 🎉</h2>
                <p className="text-green-50">Your vote for {nominee.name} has been counted</p>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-slate-700" data-testid="thank-you-message">
                  Thank you for spending your time to vote! Your support means the world to {nominee.name.split(' ')[0]} and helps celebrate excellence in Talent Acquisition.
                </p>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-sm text-purple-700">
                  💜 Know someone else who deserves recognition? You have up to 3 votes — one per category.
                </div>
                <div className="flex gap-3">
                  <Button
                    data-testid="thank-you-share-btn"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank', 'noopener,width=600,height=600')}
                    className="flex-1 bg-[#0A66C2] hover:bg-[#004182] text-white font-bold"
                  >
                    <Linkedin className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button data-testid="thank-you-done-btn" onClick={() => setShowThankYou(false)} variant="outline" className="flex-1 font-bold">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Answers */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl">{nominee.name}'s Journey</CardTitle>
            <p className="text-slate-600">Read about their achievements and approach to recruitment</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(nominee.answers).map(([key, answer], idx) => (
              <div key={key} className="border-l-4 border-purple-500 pl-4 py-2">
                <h4 className="font-bold text-slate-900 mb-2 flex items-start gap-2">
                  <span className="bg-purple-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                    {idx + 1}
                  </span>
                  {questions[idx]}
                </h4>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{answer}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NomineeProfilePage;
